<template>
  <div class="task-stage-page">
    <div v-if="selectedTask.status !== 'completed'" class="task-next" data-qid="task-qa-not-ready">
      <font-awesome-icon icon="info-circle" class="mr-1"/>
      <span class="task-next__text">{{ projectLocked ? $t('tasks.v2.qa_not_finalized_locked') : $t('tasks.v2.qa_not_ready') }}</span>
    </div>

    <!-- What the reviewers sign off -->
    <section v-else class="v2-field" data-qid="task-qa-conclusion">
      <header class="v2-field__head">
        <h4>{{ $t('tasks.v2.conclusion_to_review') }}</h4>
        <span v-if="selectedTask.response" class="task-response-pill">
          <font-awesome-icon v-if="responseIcon"
                             :icon="responseIcon.icon"
                             :style="responseIcon.style"
                             class="mr-1"
          />{{ selectedTask.response }}
        </span>
      </header>
      <div v-if="selectedTask.remarks" class="v2-field__text text-formatted"><FormattedNoteText :value="selectedTask.remarks"/></div>
      <div v-else class="v2-field__empty">{{ $t('tasks.v2.empty_field') }}</div>
      <div class="v2-field__foot" v-if="selectedTask.completed_by || selectedTask.completed_at">
        {{ $t('tasks.v2.finalized_by', { name: selectedTask.completed_by ? selectedTask.completed_by.name : '–' }) }}
        <span v-if="selectedTask.completed_at"> · {{ selectedTask.completed_at | formatDate('DD MMM', true) }}</span>
      </div>
    </section>

    <!-- Sign-offs: one level per required sign-off -->
    <section v-if="selectedTask.status === 'completed'" class="v2-field" data-qid="task-qa-sign-offs">
      <header class="v2-field__head">
        <h4>{{ $t('tasks.v2.sign_offs_title') }}</h4>
        <span class="v2-field__meta">{{ $tc('tasks.v2.required_count', neededSignOffs, { count: neededSignOffs }) }}</span>
      </header>

      <div v-if="selectedTask.qa_status === 'rejected'" class="task-level task-level--rejected">
        <span class="task-level__dot"><font-awesome-icon icon="times"/></span>
        <div class="task-level__text">
          <b>{{ $t('system.rejected') }}</b>
          <div class="task-level__sub">{{ $t('tasks.v2.rejected_hint') }}</div>
        </div>
      </div>

      <template v-else>
        <div v-for="level in levels"
             :key="'level-' + level.no"
             class="task-level"
             :class="'task-level--' + level.state">
          <span class="task-level__dot">
            <font-awesome-icon v-if="level.state === 'done'" icon="check"/>
            <span v-else>{{ level.no }}</span>
          </span>
          <div class="task-level__text">
            <b>{{ $t('tasks.v2.level', { no: level.no }) }}</b>
            <span v-if="level.signer"> · {{ level.signer.name }}</span>
            <span v-else-if="level.state === 'you'"> · {{ $t('tasks.v2.you') }}</span>
            <div class="task-level__sub">
              <span v-if="level.signer">{{ $t('tasks.v2.signed_off_at', { date: $options.filters.formatDateTime(level.signer.signed_at) }) }}</span>
              <span v-else-if="level.state === 'you'">{{ $t('tasks.v2.sign_off_hint') }}</span>
              <span v-else>{{ $t('tasks.v2.waiting_for_sign_off') }}</span>
            </div>
          </div>
          <!-- A reviewer who signed off can still change their mind -->
          <b-button v-if="level.signer && isMine(level.signer) && canAct"
                    size="sm"
                    variant="secondary"
                    :disabled="buttonDisabled"
                    @click="showReopenQaTask"
                    data-qid="task-qa-reject-own"
          >{{ $t('system.reject_and_reopen') }}</b-button>
          <template v-if="level.state === 'you'">
            <b-button size="sm"
                      variant="secondary"
                      :disabled="buttonDisabled"
                      @click="showReopenQaTask"
                      data-qid="task-qa-reject">{{ $t('system.reject_and_reopen') }}</b-button>
            <b-button size="sm"
                      variant="success"
                      :disabled="buttonDisabled"
                      @click="acceptQaTask"
                      data-qid="task-qa-sign-off">{{ $t('tasks.v2.sign_off') }}</b-button>
          </template>
        </div>
        <!-- Signed off by everyone needed, but a reviewer can still reject -->
        <div v-if="signOffReached && canAct && !isCurrentSigner" class="task-level__reject">
          <span class="v2-link cursor-pointer" @click="showReopenQaTask">{{ $t('system.reject_and_reopen') }}</span>
        </div>
      </template>
    </section>

    <!-- Quality control log -->
    <section class="v2-field" data-qid="task-qa-log">
      <header class="v2-field__head">
        <h4>{{ $t('system.quality_control_log') }}</h4>
        <span v-if="qaComments && qaComments.length > 8" class="v2-link cursor-pointer" @click="showQaLog">{{ $t('system.show_all') }} ({{ qaComments.length }})</span>
      </header>
      <QaLogTimeline v-if="qaComments && qaComments.length"
                     :entries="qaComments"
                     :limit="8"
                     compact></QaLogTimeline>
      <div v-else-if="loadingQaComments"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
      <div v-else class="v2-field__empty">{{ $t('tasks.v2.no_qa_entries') }}</div>
    </section>
  </div>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import QaLogTimeline from '@/components/tasks/QaLogTimeline.vue';
import { responseIcon } from './responseIcon';
import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';

// QA sign-off page of the task panel
export default {
  name: 'EngagementTaskQa',
  mixins: [assessmentSingleTaskProxy],
  components: {
    FormattedNoteText,
    QaLogTimeline
  },
  methods: {
    isMine(signer) {
      return !!this.getUser && signer.user_id === this.getUser.id;
    }
  },
  computed: {
    responseIcon() {
      return responseIcon(this.selectedTask.response_type_options, this.selectedTask.response);
    },
    // The current user may sign off or reject
    canAct() {
      return this.selectedTask.status === 'completed' && !this.projectLocked && this.selectedTask.can_sign_off !== false && this.selectedTask.qa_status !== 'rejected';
    },
    // 0 required sign-offs means one is enough (as in the project settings and on Home)
    neededSignOffs() {
      return Math.max(1, this.requiredSignOffs);
    },
    levels() {
      const signers = this.selectedTask.signers || [];
      const total = Math.max(this.neededSignOffs, signers.length);
      let levels = [];
      let youShown = false;
      for (let i = 0; i < total; i++) {
        const signer = signers[i];
        let state = 'todo';
        if (signer) {
          state = 'done';
        } else if (!youShown && this.canAct && !this.isCurrentSigner) {
          state = 'you';
          youShown = true;
        }
        levels.push({ no: i + 1, signer: signer, state: state });
      }
      return levels;
    }
  }
};
</script>
